import { useSocketCallback } from "@/hooks/useSocketCallback";
import { SystemInfo } from "@ailenax-overlay/shared";
import { FC, useMemo, useState } from "react";
import { MdConstruction } from "react-icons/md";

export const Home: FC = () => {
    const [systemInfo, setSystemInfo] = useState<SystemInfo>({
        STREAMELEMENTS_JWT_EXPIRY: undefined,
    });
    useSocketCallback("get-system-info", (info) => {
        setSystemInfo(info);
    });

    const streamElementsExpiryColor = useMemo(() => {
        if (!systemInfo.STREAMELEMENTS_JWT_EXPIRY) {
            return null;
        }

        const now = new Date();
        const timeDiff = systemInfo.STREAMELEMENTS_JWT_EXPIRY.getTime() - now.getTime();
        if (timeDiff <= 0) {
            return "bg-red-800";
        } else if (timeDiff <= 7*24*3600 * 1000) { // Less than or equal to 1 week
            return "bg-yellow-800";
        }
        return "bg-green-800";
    }, [systemInfo.STREAMELEMENTS_JWT_EXPIRY]);

    return (
        <div className="min-h-screen min-w-xl flex flex-col items-center justify-center">
            <h1 className="text-4xl text-center" aria-label="Under Construction">
                <MdConstruction style={{ display: "inline-block" }} color="#f7cd4c" title="Under Construction"/>
                <span>DISCLAIMER</span>
                <MdConstruction style={{ display: "inline-block" }} color="#f7cd4c" title="Under Construction"/>
            </h1>
            <p className="mt-5 text-center">
                This service is still under heavy construction.<br/>
                <br/>
                <span className="font-bold text-red-800">No guarantees are made about the content or functionality of this service.</span><br/>
                <br/>
                Like what I'm building? Feel free to leave a tip: <a className="link" href="https://www.paypal.com/ncp/payment/5NYEPSTTFK4KW" target="_blank" rel="noreferrer">PayPal</a>
            </p>
            <hr className="w-full my-5 bg-white"/>
            { systemInfo.STREAMELEMENTS_JWT_EXPIRY && (
                <>
                    <div className="w-full grid gap-2">
                        <form className={`mx-auto flex flex-col ${streamElementsExpiryColor} p-5 rounded-3xl`}>
                            <div className="relative z-0 flex-2/6">
                                StreamElements Token Expiry: { systemInfo.STREAMELEMENTS_JWT_EXPIRY.toLocaleString("de-DE", { month: "2-digit", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) }
                            </div>
                        </form>
                    </div>
                    <hr className="w-full my-5 bg-white"/>
                </>
            )}
            <div className="mt-3 w-2/3">
                <span className="font-bold text-green-600">Update (2026-05-30):</span>
                <ul className="list-disc list-inside mt-3">
                    <li>Added "Create Goal"-Page</li>
                    <li>Added "Delete Goal"-Button to "Goal"-Page</li>
                </ul>
            </div>
            <div className="mt-3 w-2/3">
                <span className="font-bold text-green-600">Update (2026-04-16):</span>
                <ul className="list-disc list-inside mt-3">
                    <li>
                        Secured StreamDeck-Action Routes
                        <ul className="list-disc list-inside ml-10">
                            <li>StreamDeck-Action Routes now require the Overlay Access Token in order to function.</li>
                            <li>This prevents unauthorized triggering of StreamDeck-Actions from external sources.</li>
                            <li>If you need assistance with migrating your StreamDeck actions, give me a ping via Discord ({import.meta.env.VITE_BOT_OWNER})</li>
                        </ul>
                    </li>
                </ul>
            </div>
            <div className="mt-3 w-2/3">
                <span className="font-bold text-green-600">Update (2026-04-10):</span>
                <ul className="list-disc list-inside mt-3">
                    <li>Added Tipeee Setup and Help-Page. Tipeee integration can now be fully set up by the Streamer.</li>
                </ul>
            </div>
            <div className="mt-3 w-2/3">
                <span className="font-bold text-green-600">Update (2026-04-05):</span>
                <ul className="list-disc list-inside mt-3">
                    <li>Fixed Login</li>
                    <li>Added support for Sub- and Bit-Goals</li>
                    <li>Added USD ($) to supported currencies</li>
                    <li>Added basic localization support to the Donation-Train (english & german for now)</li>
                    <li>Added Ko-fi Setup and Help-Page. Ko-fi integration can now be fully set up by the Streamer.</li>
                    <li>Added StreamElements Setup and Help-Page. StreamElements integration can now be fully set up by the Streamer.</li>
                    <li>Added Shared-Chat Support to Goals (Subs & Bits). Streamers can now decide wether events in shared chat (for other streamers) should be counted towards their goals.</li>
                    <li>Added Session Management</li>
                    <li>Added 'Reset on Session-Start'-Setting to Goal. This enables Streamers to always start with fresh goals.</li>
                    <li>
                        Added Chat-Bot Configuration
                        <ul className="list-disc list-inside ml-10">
                            <li>Donation and Ko-fi Re-Sub Alerts</li>
                            <li>Donation-Train Ready/Primed/Start/End Alerts</li>
                            <li>Donation-Train Level-Up Alerts per Level</li>
                        </ul>
                    </li>
                </ul>
            </div>
            <div className="mt-12 w-2/3">
                <span className="font-bold">Current State:</span>
                <ul className="list-disc list-inside mt-3">
                    <li> Donations
                        <ul className="list-disc list-inside ml-10">
                            <li>
                                Support for Donations from Ko-fi, StreamElements and Tipeee
                                <ul className="list-disc list-inside ml-10">
                                    <li>At this moment setting up Donation Providers requires some manual work from the Streamer and me until I get around to implementing proper Setup Processes.</li>
                                </ul>
                            </li>
                            <li>
                                Fallback Support for Donations via Bot-Chat-Messages
                                <ul className="list-disc list-inside ml-10">
                                    <li>This way practically any Provider can be supported as long as there's a Bot posting messages into your Channel.</li>
                                </ul>
                            </li>
                            <li>
                                <span className="text-yellow-400">Under Construction (short-term)</span>:
                                <ul className="list-disc list-inside ml-10">
                                    <li className="line-through">Support for currencies other than Euro (€)</li>
                                    <li>Linking Accounts for StreamElements and later Streamlabs (at this moment a manual setup is still required)</li>
                                    <li>Help Page setting up Tipeee Donations using API Token (at this moment a manual setup is still required)</li>
                                    <li className="line-through">Help Page setting up Ko-fi Donations</li>
                                    <li>Allow managing the messages the Bots from your Donation Providers send to recognize external donations</li>
                                </ul>
                            </li>
                        </ul>
                    </li>
                    <li>
                        Donation-Train
                        <ul className="list-disc list-inside ml-10">
                            <li>Full credits for the Donation-Train go to <a className="link" href="https://twitch.tv/ailenax">@Ailenax on Twitch.tv</a></li>
                            <li>Train-Levels up to Level 30</li>
                            <li>Customizable Durations</li>
                            <li>
                                <span className="text-yellow-400">Under Construction (short-term)</span>:
                                <ul className="list-disc list-inside ml-10">
                                    <li>Improved customizability (styling)</li>
                                </ul>
                            </li>
                        </ul>
                    </li>
                    <li>
                        Goals
                        <ul className="list-disc list-inside ml-10">
                            <li>Inspired by the needs of <a className="link" href="https://twitch.tv/dirkneuenfels">@DirkNeuenfels on Twitch.tv</a></li>
                            <li className="line-through">At this moment only Donation-Goals are supported.</li>
                            <li>Donations from all providers (StreamElements, Ko-fi, Tipeee, ...) automatically count towards the goals</li>
                            <li>
                                <span className="text-yellow-400">Under Construction (short-term)</span>:
                                <ul className="list-disc list-inside ml-10">
                                    <li>"Create Goal"-Page (at this moment I have to manually setup multiple goals</li>
                                    <li>Improved customizability (styling)</li>
                                    <li className="line-through">Support for Sub- and Bit-Goals</li>
                                </ul>
                            </li>
                        </ul>
                    </li>
                    <li>
                        <span className="text-red-400">Necessities (short-term)</span>:
                        <ul className="list-disc list-inside ml-10">
                            <li>
                                Legal (Privacy Policy, Terms of Service, ...)
                                <ul className="list-disc list-inside ml-10">
                                    <li>We all hate those, yet they're still needed for a professional relationship.</li>
                                    <li>In the meantime: This service is provided "as-is" and no guarantees are made on current or future functionality.</li>
                                    <li>Consider this an "early access", your Feedback and Requests will shape this service.</li>
                                    <li>With that said: I'll do my best to maintain a working solution and communicate big changes beforehand.</li>
                                </ul>
                            </li>
                            <li>
                                Centralized Communication Channel
                                <ul className="list-disc list-inside ml-10">
                                    <li>Necessary for News, Bug-Reports, Feature Requests, Commissions etc.</li>
                                    <li>In the meantime: Ping me on Discord (cckep) or Twitch ({import.meta.env.VITE_BOT_OWNER})</li>
                                </ul>
                            </li>
                        </ul>
                    </li>
                    <li>
                        <span className="text-yellow-400">Planned Features (mid-term)</span>:
                        <ul className="list-disc list-inside ml-10">
                            <li>Filterable real-time Activity-Feed</li>
                            <li>Alerts</li>
                            <li>Dashboard and Analytics</li>
                        </ul>
                    </li>
                    <li>
                        <span className="text-yellow-400">Planned Features (long-term)</span>:
                        <ul className="list-disc list-inside ml-10">
                            <li>Proper User- and Session-System</li>
                            <li>Redesigning the Configuration Pages and building a responsive layout</li>
                            <li><span className="line-through">Multi-Channel support</span> Not happening.</li>
                            <li>Chat-Bot functionality (Commands, Timers, Counters, ...)</li>
                            <li>Various Exports (csv)</li>
                        </ul>
                    </li>
                    <li>
                        <span className="text-yellow-400">Planned Features (distant future, lower priority for now)</span>:
                        <ul className="list-disc list-inside ml-10">
                            <li className="line-through">Support for different languages (i18n)</li>
                            <li>Support for sharing access and multiple Roles (like Editor or Moderator etc.)</li>
                        </ul>
                    </li>
                </ul>
            </div>
            <p className="mt-5 text-center">
                Missing something? Individual Requests?<br/>
                Feel free to ping me on Discord (cckep) or Twitch ({import.meta.env.VITE_BOT_OWNER})!
            </p>
        </div>
    );
}